import { useCallback, useState, FC } from "react";

import { Button, Card } from "react-bootstrap";
import { FieldValues, useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";

import { UseMutateFunction } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { FileUpload } from "primereact/fileupload";
import { Image } from "primereact/image";

import Input from "@/common/components/form/Input";
import SearchAddress from "@/common/components/form/SearchAddress";
import Select from "@/common/components/form/SelectForm.tsx";
import Loader from "@/common/components/Loader.tsx";
import MapComponent from "@/common/components/map/MapComponent";
import { invoiceFrequencyOptions } from "@/common/constants/index.ts";
import { Permissions } from "@/common/enums/sharedEnums.ts";
import { emailRule, phoneRule } from "@/common/formValidation/index.ts";
import { useGetUserType } from "@/modules/auth/hooks/userType.hooks.ts";

import { SCvalues } from "../../../_helpers/commonComponentsTypes.ts";
import edit from "../../../assets/svg/edit.svg";
import history from "../../../routes/history";
import { IAgency } from "../interfaces/agency";
import { AGENCY, MANAGEMENT } from "../routes/ProfileRoutes";
import { uploadAgencyLogo } from "../services";

interface IAgencyFormProps {
  defaultValues?: IAgency;
  groupId?: string;
  readOnly?: boolean;
  offerTypeOptions: SCvalues[] | [];
  isLoadingOfferTypeData?: boolean;
  isLoadingCreateAgency?: boolean;
  isLoadingUpdateAgency?: boolean;
  billingDayOptions: SCvalues[];
  createAgency: UseMutateFunction<
    AxiosResponse<IAgency, unknown>,
    Error,
    IAgency,
    unknown
  >;
  updateAgency: UseMutateFunction<
    AxiosResponse<IAgency, unknown>,
    Error,
    IAgency,
    unknown
  >;
}

const AgencyForm: FC<IAgencyFormProps> = ({
  defaultValues,
  groupId,
  readOnly,
  offerTypeOptions,
  isLoadingOfferTypeData,
  isLoadingCreateAgency,
  isLoadingUpdateAgency,
  billingDayOptions,
  createAgency,
  updateAgency,
}) => {
  const { isSuperAdmin } = useGetUserType();

  const { handleSubmit, control, watch } = useForm<FieldValues>({
    defaultValues,
  });

  const [logoUrl, setLogoUrl] = useState<string>(watch("logo.url"));

  const onSubmit = async (data: any) => {
    try {
      delete data.teams;
      if (data?.id) {
        // update
        updateAgency({
          ...data,
          customer: {
            id: data?.customer?.id,
            source_id: data?.customer?.sourceId,
            offer_type_id: data?.offer_type_id?.value,
            invoice_frequency_edl: data?.invoice_frequency_edl?.value,
            invoice_frequency_erp: data?.invoice_frequency_erp?.value,
            billingDay: data?.billingDay?.value,
          },
        });
      } else {
        if (!groupId) {
          toast.error("Groupe non trouvé.");
          return;
        }
        data.groupId = groupId;
        createAgency({
          ...data,
          customer: {
            offer_type_id: data?.offer_type_id?.value,
            invoice_frequency_edl: data?.invoice_frequency_edl?.value,
            invoice_frequency_erp: data?.invoice_frequency_erp?.value,
            billingDay: data?.billingDay?.value,
          },
        });
      }
      history.push(MANAGEMENT);
    } catch (e: any) {
      toast.error("Impossible de valider le formulaire");
    }
  };

  const uploadHandler = async (data: any) => {
    const [file] = data.files;
    const fileReader = new FileReader();

    const formData = new FormData();
    formData.append("logo", file);

    await uploadAgencyLogo(watch("id"), formData).then(({ url }: any) => {
      setLogoUrl(url);
    });
    fileReader.readAsDataURL(file);

    toast.success(`Le logo a été mis à jour !`);
  };

  const MapRender = useCallback(() => {
    const addressData = watch("address");
    return (
      <MapComponent
        coordinates={
          addressData?.latitudeY && addressData?.longitudeX
            ? {
                lat: Number(addressData?.latitudeY),
                lng: Number(addressData?.longitudeX),
              }
            : undefined
        }
        style={{
          borderRadius: "16px",
          flex: 1,
        }}
      />
    );
  }, [watch]);

  return (
    <>
      <div className="bg-container agencyForm">
        <div className="d-flex justify-content-between">
          <div className=" d-flex gap-2 mb-2">
            {logoUrl && <Image src={logoUrl} alt="Image" width="50" preview />}
            <h2
              className="mt-1 fw-bold align-self-center"
              style={{ fontSize: "24px" }}
            >
              {defaultValues
                ? `Agence ${defaultValues.label}`
                : "Nouvelle Agence"}
            </h2>
          </div>
          {readOnly &&
            defaultValues?.permissions?.includes(Permissions.AGENCY_UPDATE) && (
              <Link
                to={{
                  pathname: AGENCY.replace(":id?", defaultValues?.id || ""),
                  state: { readOnly: false, parentId: groupId },
                }}
                replace
                className="btn btn-actions btn-edit-order"
                style={{ height: "36px", width: "36px" }}
              >
                <img
                  src={edit}
                  alt="edit"
                  style={{ width: "12px", height: "12px" }}
                />
              </Link>
            )}
        </div>
        <form onSubmit={handleSubmit(onSubmit)} className="agencyForm-form">
          <div className="d-flex align-items-center" >
          <div className="agencyForm-form-info d-flex gap-3 align-items-end row-12 p-2 grow-1">
            <div className="d-flex gap-3 col-10">
              <Input
                control={control}
                label="Nom"
                name={"label"}
                defaultValue=""
                rules={{ required: "Veuillez renseigner un nom" }}
                disabled={readOnly}
              />
              <Input
                control={control}
                label="Email"
                name={"email"}
                defaultValue=""
                rules={{
                  ...emailRule,
                }}
                disabled={readOnly}
              />
              <Input
                control={control}
                label="Téléphone"
                name={"phone"}
                defaultValue=""
                disabled={readOnly}
                rules={{
                  required: "Veuillez renseigner un numéro de téléphone",
                  ...phoneRule,
                }}
              />
              <Input
                control={control}
                label="Siren"
                name={"siren"}
                defaultValue=""
                disabled={readOnly}
              />
            </div>
          </div>
          <div className='d-flex'>
              {watch("id") && !readOnly && (
                <FileUpload
                  auto
                  mode="basic"
                  name="logo"
                  accept="image/*"
                  maxFileSize={2000000}
                  customUpload
                  uploadHandler={uploadHandler}
                  chooseLabel="Ajouter / Modifier le logo"
                />
              )}
            </div>
            </div>
          <Card>
            <Card.Header>Emplacement</Card.Header>
            <Card.Body className="agencyForm-form-address">
              <div className="agencyForm-form-address-inputs">
                {readOnly ? (
                  <Input
                    control={control}
                    label="Adresse postale"
                    name="address"
                    defaultValue={
                      defaultValues?.address &&
                      `${defaultValues.address.address} ${defaultValues.address.zip} ${defaultValues.address.city}`
                    }
                    disabled={true}
                  />
                ) : (
                  <SearchAddress
                    control={control}
                    name={`address`}
                    rules={{ required: "veuillez indiquer une adresse" }}
                    label="Adresse postale"
                    isDisabled={false}
                    defaultAddress=""
                  />
                )}

                <Input
                  control={control}
                  label="Adresse additionnelle"
                  name={"address.additionalAddress"}
                  defaultValue=""
                  disabled={readOnly}
                />
                <Input
                  control={control}
                  label="Digicode"
                  name={"address.digicode"}
                  defaultValue=""
                  disabled={readOnly}
                />
                <Input
                  control={control}
                  label="Résidence"
                  name={"address.residenceName"}
                  defaultValue=""
                  disabled={readOnly}
                />
              </div>
              <MapRender />
            </Card.Body>
          </Card>
          {isSuperAdmin && (
            <Card>
              <Card.Header>Facturation</Card.Header>
              <Card.Body className="agencyForm-customer">
                <div className="agencyForm-form-address-inputs">
                  <Select
                    icon={<i className="pi pi-cog"></i>}
                    options={invoiceFrequencyOptions}
                    control={control}
                    label="Fréquence de facturation EDL"
                    name="invoice_frequency_edl"
                    defaultValue={invoiceFrequencyOptions.find(
                      (option) =>
                        option.value ===
                        defaultValues?.customer?.invoiceFrequencyEdl
                    )}
                    rules={{
                      required:
                        "Veuillez renseigner la fréquence de facturation EDL",
                    }}
                    disabled={readOnly}
                  />
                </div>
                <div className="agencyForm-form-address-inputs">
                  <Select
                    icon={<i className="pi pi-cog"></i>}
                    options={invoiceFrequencyOptions}
                    control={control}
                    label="Fréquence de facturation ERP"
                    name="invoice_frequency_erp"
                    defaultValue={invoiceFrequencyOptions.find(
                      (option) =>
                        option.value ===
                        defaultValues?.customer?.invoiceFrequencyErp
                    )}
                    rules={{
                      required:
                        "Veuillez renseigner la fréquence de facturation ERP ",
                    }}
                    disabled={readOnly}
                  />
                </div>
                <div className="agencyForm-form-address-inputs">
                  <Select
                    icon={<i className="pi pi-box"></i>}
                    options={offerTypeOptions}
                    control={control}
                    label="Type d'offre"
                    name={"offer_type_id"}
                    defaultValue={offerTypeOptions.find(
                      (option) =>
                        option.value === defaultValues?.customer?.offerType?.id
                    )}
                    rules={{
                      required: "Veuillez renseigner le type d'offre",
                    }}
                    disabled={readOnly}
                  />
                </div>
                <div className="agencyForm-form-address-inputs">
                  <Select
                    icon={<i className="pi pi-calendar"></i>}
                    options={billingDayOptions}
                    control={control}
                    label="Jour de facturation"
                    name={"billingDay"}
                    defaultValue={billingDayOptions.find(
                      (option) =>
                        option.value === defaultValues?.customer?.billingDay
                    )}
                    disabled={readOnly}
                  />
                </div>
              </Card.Body>
            </Card>
          )}
          {!readOnly && (
            <div className="d-flex justify-content-start">
              <Button variant={"primary"} type={"submit"}>
                {defaultValues ? "Modifier" : "Ajouter"}
              </Button>
            </div>
          )}
        </form>
      </div>
      {(isLoadingOfferTypeData ||
        isLoadingCreateAgency ||
        isLoadingUpdateAgency) && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
          style={{ backgroundColor: "rgba(255, 255, 255, 0.5)" }}
        >
          <Loader />
        </div>
      )}
    </>
  );
};

export default AgencyForm;
